import { AfterViewInit, Component } from "@angular/core";
import { ConfirmationService, MessageService } from "primeng/api";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { Filing, Form, TradingPlan } from "src/api/models";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { AsReportedSecurityBreakdownService } from "src/api/services";
import { PlanAction, TransactionType } from "../../../../../api/models";

@Component({
  selector: "app-edit-data",
  templateUrl: "./edit-data.component.html",
  styleUrl: "./edit-data.component.css",
  standalone: false,
})
export class EditDataComponent implements AfterViewInit {
  first = 0;
  rows = 10;
  readonly selectedPlans!: TradingPlan[];
  readonly filing: Filing = {};
  readonly form: Form = {};
  showMessage = false;
  messageText = "At least one security is required in trading plan.";
  transactionTypeOptions = TransactionType.values().map((type) => ({
    label: this.formatTransactionType(type),
    value: type,
  }));
  entityTypeOptions = [
    { label: "INDIVIDUAL", value: "INDIVIDUAL" },
    { label: "TRUST", value: "TRUST" },
    { label: "LLC", value: "LLC" },
    { label: "SPOUSE", value: "SPOUSE" },
    { label: "PARTNERSHIP", value: "PARTNERSHIP" },
    { label: "FUND", value: "FUND" },
    { label: "OTHER", value: "OTHER" },
  ];

  planActionOptions = PlanAction.values().map((type) => ({
    label: this.formatPlanAction(type),
    value: type,
  }));

  // Main form group that contains all trading plans
  formMainGroup: FormGroup;

  constructor(
    private _asReportedSecurityBreakdownService: AsReportedSecurityBreakdownService,
    private _confirmationService: ConfirmationService,
    public config: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    private _messageService: MessageService,
    private fb: FormBuilder
  ) {
    this.filing = this.config.data.filing;
    this.selectedPlans = this.config.data.selectedPlans;
    this.form = this.config.data.form;

    this.formMainGroup = this.fb.group({
      tradingPlans: this.fb.array([]),
    });
  }

  // Getter for easy access to trading plans form array
  get tradingPlans(): FormArray {
    return this.formMainGroup.get("tradingPlans") as FormArray;
  }

  ngAfterViewInit(): void {
    this.onEditClick();
  }

  formatTransactionType(type: TransactionType): string {
    switch (type) {
      case "BUY":
        return "Buy";
      case "SELL":
        return "Sell";
      case "OTHER":
        return "Other";
      default:
        return type;
    }
  }

  formatPlanAction(type: PlanAction): string {
    switch (type) {
      case "ADOPTION":
        return "Adoption";
      case "TERMINATION":
        return "Termination";
      case "AMENDMENT":
        return "Amendment";
      default:
        return type;
    }
  }

  onEditClick() {
    if (this.selectedPlans.length > 0) {
      // Clear existing form groups
      while (this.tradingPlans.length !== 0) {
        this.tradingPlans.removeAt(0);
      }

      for (let plan of this.selectedPlans) {
        const securityBreakdowns = Array.isArray(
          plan.asReportedSecurityBreakdowns
        )
          ? plan.asReportedSecurityBreakdowns
          : [];

        // Add new trading plan form group
        this.tradingPlans.push(
          this.createTradingPlanFormGroup(plan, securityBreakdowns)
        );
      }
    }
  }

  createTradingPlanFormGroup(plan: any, securityBreakdowns: any[]): FormGroup {
    const formGroup = this.fb.group({
      tradingPlanId: [plan.id || plan.tradingPlanId || null],
      insiderName: [
        plan.asReportedInsider?.asReportedInsiderName || "",
        Validators.required,
      ],
      asReportedReportingEntityName:
        plan.asReportedInsider?.asReportedReportingEntityName || null,
      asReportedReportingEntityType:
        plan.asReportedInsider?.asReportedReportingEntityType || null,
      asReportedInsiderRoleInEntity:
        plan.asReportedInsider?.asReportedInsiderRoleInEntity || null,
      planAction: [plan.planAction || "", Validators.required],
      relation: [plan.relationshipToIssuer || ""],

      durationDays: [
        plan.durationDays || "",
        [Validators.pattern(/^\d+$/), Validators.min(0)],
      ],
      asReportedPlanText: [plan.asReportedPlanText || "", Validators.required],
      insiderComment: [plan.asReportedInsider.insiderComment || ""],
      insiderId: [plan.asReportedInsider?.insiderId  || null],
      adoptionDate: [plan.adoptionDate ? new Date(plan.adoptionDate) : null],
      effectiveDate: [plan.effectiveDate ? new Date(plan.effectiveDate) : null],
      amendmentDate: [plan.amendmentDate ? new Date(plan.amendmentDate) : null],
      terminationDate: [
        plan.terminationDate ? new Date(plan.terminationDate) : null,
      ],
      expirationDate: [
        plan.expirationDate ? new Date(plan.expirationDate) : null,
      ],

      securityBreakdowns: this.fb.array([]),
    });

    // Add conditional validators based on planAction
    formGroup.get("planAction")?.valueChanges.subscribe((action) => {
      const adoptionDateControl = formGroup.get("adoptionDate");
      const terminationDateControl = formGroup.get("terminationDate");
      const amendmentDateControl = formGroup.get("amendmentDate");

      // Reset validators first
      adoptionDateControl?.clearValidators();
      terminationDateControl?.clearValidators();
      amendmentDateControl?.clearValidators();

      // Set validators based on action
      switch (action) {
        case "ADOPTION":
          adoptionDateControl?.setValidators(Validators.required);
          break;
        case "TERMINATION":
          terminationDateControl?.setValidators(Validators.required);
          break;
        case "AMENDMENT":
          amendmentDateControl?.setValidators(Validators.required);
          break;
      }

      // Update control validity
      adoptionDateControl?.updateValueAndValidity();
      terminationDateControl?.updateValueAndValidity();
      amendmentDateControl?.updateValueAndValidity();
    });

    // Trigger initial validation
    formGroup.get("planAction")?.updateValueAndValidity();

    // Add security breakdowns
    const breakdownArray = formGroup.get("securityBreakdowns") as FormArray;
    for (let breakdown of securityBreakdowns) {
      breakdownArray.push(this.createBreakdownFormGroup(breakdown, plan));
    }

    return formGroup;
  }
  createBreakdownFormGroup(breakdown: any, plan: any): FormGroup {
    return this.fb.group({
      tradingPlanId: [plan.id || plan.tradingPlanId || null],
      asReportedSecurityBreakdownId: [
        breakdown?.asReportedSecurityBreakdownId || null,
      ],
      securityType: [
        breakdown.asReportedSecurity?.asReportedSecurityType || "",
        Validators.required,
      ],
      transactionType: [breakdown.transactionType || "", Validators.required],
      numberOfShares: [breakdown.numberOfShares || null, [Validators.min(0)]],
      notionalValue: [breakdown.notionalValue || null],

      tradingConditions: [plan.tradingConditions || ""],
      earlyTerminationConditions: [plan.earlyTerminationConditions || ""],
      sharesDescription: [breakdown.sharesDescription || ""],
    });
  }

  // addTradingPlan() {
  //   this.tradingPlans.push(this.createEmptyTradingPlanFormGroup());
  // }
  addTradingPlan() {
    // Agar already koi plan exist karta hai
    if (this.tradingPlans.length > 0) {
      const lastPlan = this.tradingPlans.at(this.tradingPlans.length - 1).value;

      this.tradingPlans.push(this.createClonedTradingPlanFormGroup(lastPlan));
    } else {
      // Pehla plan blank hi ho
      this.tradingPlans.push(this.createEmptyTradingPlanFormGroup());
    }
  }

  createEmptyTradingPlanFormGroup(): FormGroup {
    const formGroup = this.fb.group({
      tradingPlanId: [null],
      insiderName: ["", Validators.required],
      asReportedReportingEntityName: null,
      asReportedReportingEntityType: null,
      asReportedInsiderRoleInEntity: null,
      planAction: ["", Validators.required],
      relation: [""],
      adoptionDate: null,
      effectiveDate: null,
      durationDays: ["", [Validators.pattern(/^\d+$/), Validators.min(0)]],
      asReportedPlanText: ["", Validators.required],
      insiderComment:[""],
      terminationDate: null,
      amendmentDate: null,
      expirationDate: null,
      securityBreakdowns: this.fb.array([this.createEmptyBreakdownFormGroup()]),
    });

    formGroup.get("planAction")?.valueChanges.subscribe((action) => {
      const adoptionDateControl = formGroup.get("adoptionDate");
      const terminationDateControl = formGroup.get("terminationDate");
      const amendmentDateControl = formGroup.get("amendmentDate");

      adoptionDateControl?.clearValidators();
      terminationDateControl?.clearValidators();
      amendmentDateControl?.clearValidators();

      switch (action) {
        case "ADOPTION":
          adoptionDateControl?.setValidators(Validators.required);
          break;
        case "TERMINATION":
          terminationDateControl?.setValidators(Validators.required);
          break;
        case "AMENDMENT":
          amendmentDateControl?.setValidators(Validators.required);
          break;
      }

      adoptionDateControl?.updateValueAndValidity();
      terminationDateControl?.updateValueAndValidity();
      amendmentDateControl?.updateValueAndValidity();
    });

    return formGroup;
  }
  createClonedTradingPlanFormGroup(plan: any): FormGroup {
    return this.fb.group({
      tradingPlanId: [null],
      insiderName: [plan.insiderName || "", Validators.required],
      asReportedReportingEntityName: plan.asReportedReportingEntityName || null,
      asReportedReportingEntityType: plan.asReportedReportingEntityType || null,
      asReportedInsiderRoleInEntity: plan.asReportedInsiderRoleInEntity || null,
      planAction: [plan.planAction || "", Validators.required],
      relation: [plan.relation || ""],
      adoptionDate: plan.adoptionDate || null,
      effectiveDate: plan.effectiveDate || null,
      durationDays: [
        plan.durationDays || "",
        [Validators.pattern(/^\d+$/), Validators.min(0)],
      ],
      asReportedPlanText: [plan.asReportedPlanText || "", Validators.required],
      insiderComment: [plan.insiderComment || ""],

      terminationDate: plan.terminationDate || null,
      amendmentDate: plan.amendmentDate || null,
      expirationDate: plan.expirationDate || null,

      // ⚡️ Breakdown hamesha empty array
      securityBreakdowns: this.fb.array([]),
    });
  }
  createEmptyBreakdownFormGroup(tradingPlanId: any = null): FormGroup {
    return this.fb.group({
      tradingPlanId: [null],
      asReportedSecurityBreakdownId: [null],
      securityType: ["", Validators.required],
      transactionType: ["", Validators.required],
      numberOfShares: [null, [Validators.min(0)]],
      notionalValue: [null],

      tradingConditions: [""],
      earlyTerminationConditions: [""],
      sharesDescription: [""],
    });
  }

  addSecurityBreakdown(index: number) {
    this.showMessage = false;
    const breakdownArray = this.getBreakdownControls(index);
    const planForm = this.tradingPlans.at(index) as FormGroup;

    // Get the tradingPlanId from the current plan form
    const planId = planForm.get("tradingPlanId")?.value;

    const newBreakdown = this.createEmptyBreakdownFormGroup(planId);
    breakdownArray.push(newBreakdown);

    // Mark the form as dirty since we've made a change
    this.formMainGroup.markAsDirty();
  }

  getBreakdownControls(index: number): FormArray {
    return this.tradingPlans.at(index).get("securityBreakdowns") as FormArray;
  }

  removeSecurityBreakdown(rowIndex: number, breakdownIndex: number): void {
    const breakdownArray = this.getBreakdownControls(rowIndex);
    const breakdownControl = breakdownArray.at(breakdownIndex);
    const tradingPlanId = breakdownControl.value.tradingPlanId;
    if (!tradingPlanId) {
      breakdownArray.removeAt(breakdownIndex);
      return;
    }

    this._confirmationService.confirm({
      message: `<div class="font-semibold text-base">
            Are you sure you want to delete this <strong>security breakdown</strong>?<br/>
            This action <u>cannot be undone</u>.
          </div>`,
      header: "Confirm Deletion",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      rejectButtonStyleClass: "p-button-secondary p-button-sm",
      acceptButtonStyleClass: "p-button-danger p-button-sm",
      closable: true,
      rejectVisible: true,
      accept: () => {
        const originalPlan = this.selectedPlans[rowIndex];
        const originalBreakdown =
          originalPlan?.asReportedSecurityBreakdowns?.[breakdownIndex];

        if (originalBreakdown?.asReportedSecurityBreakdownId) {
          this._asReportedSecurityBreakdownService
            .deleteAsReportedSecurityBreakdown({
              asReportedSecurityBreakdownId:
                originalBreakdown.asReportedSecurityBreakdownId,
              reason: "Incorrect plan created by LLM",
            })
            .subscribe({
              next: (response: boolean) => {
                if (response) {
                  breakdownArray.removeAt(breakdownIndex);
                  this._messageService.add({
                    severity: "success",
                    summary: "Deleted",
                    detail: "Breakdown deleted successfully",
                  });
                } else {
                  this._messageService.add({
                    severity: "error",
                    summary: "Failed",
                    detail: "Breakdown could not be deleted",
                  });
                }
              },
              error: (err: any) => {
                this._messageService.add({
                  severity: "error",
                  summary: "Error",
                  detail: "An error occurred while deleting",
                });
                console.error(err);
              },
            });
        } else {
          // If no ID exists, just remove from the form
          breakdownArray.removeAt(breakdownIndex);
          this._messageService.add({
            severity: "warn",
            summary: "Removed",
            detail: "Breakdown removed from form (not deleted from server)",
          });
        }
      },
    });
  }
  removeTradingPlan(index: number): void {
    this.tradingPlans.removeAt(index);
  }
  updateForm() {
    let isValid = true;
    this.markFormGroupTouched(this.formMainGroup);
    const formValue = this.formMainGroup.value;
    // Check if any trading plan has empty securityBreakdowns
    const hasEmptySecurity = formValue.tradingPlans.some(
      (plan: any, index: number) => {
        const breakdowns = plan.securityBreakdowns;
        if (!breakdowns || breakdowns.length === 0) {
          this.showMessage = true;

          return true;
        }
        return false;
      }
    );

    if (hasEmptySecurity) {
      isValid = false;
      return;
    }

    const updatedTradingPlans: any[] = [];

    for (let i = 0; i < this.tradingPlans.length; i++) {
      const planForm = this.tradingPlans.at(i) as FormGroup;

      if (planForm.invalid) {
        isValid = false;
        this._messageService.add({
          severity: "error",
          summary: "Error",
          detail: `Please fill all required fields in Trading Plan ${i + 1}`,
        });
      }

      const planValue = planForm.value;
      // const planId = planForm.get("tradingPlanId")?.value || undefined;
let planId = planForm.get("tradingPlanId")?.value;

// If planId is missing, generate a new one dynamically
if (!planId) {
  planId = this.generateDynamicPlanId();
  planForm.get("tradingPlanId")?.setValue(planId);

  // Also assign it to all child breakdowns if needed
  const breakdownArray = planForm.get("securityBreakdowns") as FormArray;
  breakdownArray.controls.forEach((bd) => {
    bd.get("tradingPlanId")?.setValue(planId);
  });
}
      const originalPlan =
        this.filing?.tradingPlans?.find(
          (p: any) => p.id === planId || p.tradingPlanId === planId
        ) || {};

      const breakdownArray = planForm.get("securityBreakdowns") as FormArray;

      const updatedBreakdowns: any[] = [];

      for (let j = 0; j < breakdownArray.length; j++) {
        const bdForm = breakdownArray.at(j) as FormGroup;

        if (bdForm.invalid) {
          isValid = false;
          this._messageService.add({
            severity: "error",
            summary: "Error",
            detail: `Please fill all required fields in Security Breakdown ${
              j + 1
            } of Trading Plan ${i + 1}`,
          });
        }

        const bdValue = bdForm.value;

        const originalBreakdown =
          originalPlan?.asReportedSecurityBreakdowns?.find(
            (bd: any) =>
              bd?.asReportedSecurityBreakdownId ===
              bdForm.get("asReportedSecurityBreakdownId")?.value
          ) || {};

        updatedBreakdowns.push({
          ...originalBreakdown,
          asReportedSecurityBreakdownId:
            originalBreakdown?.asReportedSecurityBreakdownId || undefined,
          tradingPlanId: planId,
          transactionType: bdValue.transactionType || undefined,
          asReportedSecurity: {
            ...(originalBreakdown.asReportedSecurity || {}),
            asReportedSecurityType: bdValue.securityType || undefined,
          },
          numberOfShares: bdValue.numberOfShares || undefined,
          notionalValue: bdValue.notionalValue || undefined,
          sharesDescription: bdValue.sharesDescription || undefined,
        });
      }
      //  Calculate expiration date automatically if ADOPTION plan
      let calculatedExpirationDate = planValue.expirationDate;

      if (
        planValue.planAction === "ADOPTION" &&
        planValue.adoptionDate &&
        planValue.durationDays
      ) {
        const adoptionDate = new Date(planValue.adoptionDate);
        const duration = Number(planValue.durationDays);

        if (!isNaN(duration)) {
          const expiration = new Date(adoptionDate);
          expiration.setDate(adoptionDate.getDate() + duration);
          calculatedExpirationDate = expiration;
        }
      }
      const updatedPlan = {
        ...originalPlan,
        id: planId,
        tradingPlanId: planId,
        planAction: planValue.planAction || undefined,
        relationshipToIssuer: planValue.relation || undefined,
        adoptionDate: planValue.adoptionDate || undefined,
        effectiveDate: planValue.effectiveDate || undefined,
        amendmentDate: planValue.amendmentDate || undefined,
        terminationDate: planValue.terminationDate || undefined,
        expirationDate: calculatedExpirationDate || undefined,
        // expirationDate: planValue.expirationDate || undefined,
        durationDays: planValue.durationDays
          ? Number(planValue.durationDays)
          : undefined,
        asReportedPlanText: planValue.asReportedPlanText || undefined,
      

        earlyTerminationConditions:
          breakdownArray.at(0)?.value.earlyTerminationConditions || undefined,
        tradingConditions:
          breakdownArray.at(0)?.value.tradingConditions || undefined,
        asReportedInsider: {
          ...(originalPlan.asReportedInsider || {}),
          asReportedInsiderName: planValue.insiderName || undefined,
          asReportedReportingEntityName:
            planValue.asReportedReportingEntityName || undefined,
          asReportedReportingEntityType:
            planValue.asReportedReportingEntityType || undefined,
          asReportedInsiderRoleInEntity:
            planValue.asReportedInsiderRoleInEntity || undefined,
              insiderComment: planValue.insiderComment || undefined,
        },
        asReportedSecurityBreakdowns: updatedBreakdowns,
      };

      updatedTradingPlans.push(updatedPlan);
    }

    if (!isValid) return;

    const updatedFiling = {
      ...this.filing,
      tradingPlans: updatedTradingPlans,
    } as Filing;

    const data = {
      filing: updatedFiling,
      form: {
        ...this.form,
      },
    };

    this.ref.close(data);
  }

  markFormGroupTouched(formGroup: FormGroup | FormArray) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();

      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupTouched(control);
      }
    });
  }

  isFieldInvalid(formIndex: number, fieldName: string): boolean {
    const form = this.tradingPlans.at(formIndex) as FormGroup;
    const control = form.get(fieldName);
    return control
      ? control.invalid && (control.dirty || control.touched)
      : false;
  }

  isBreakdownFieldInvalid(
    rowIndex: number,
    breakdownIndex: number,
    fieldName: string
  ): boolean {
    const breakdownArray = this.getBreakdownControls(rowIndex);
    const breakdownGroup = breakdownArray.at(breakdownIndex) as FormGroup;
    const control = breakdownGroup.get(fieldName);
    return control
      ? control.invalid && (control.dirty || control.touched)
      : false;
  }

  getErrorMessage(formIndex: number, fieldName: string): string {
    const form = this.tradingPlans.at(formIndex) as FormGroup;
    const control = form.get(fieldName);
    if (control?.hasError("required")) {
      return "This field is required";
    }
    if (control?.hasError("min")) {
      return `Value must be greater than or equal to ${control.errors?.["min"].min}`;
    }
    if (control?.hasError("pattern")) {
      return "Please enter a valid number";
    }
    return "";
  }

  getBreakdownErrorMessage(
    rowIndex: number,
    breakdownIndex: number,
    fieldName: string
  ): string {
    const breakdownArray = this.getBreakdownControls(rowIndex);
    const breakdownGroup = breakdownArray.at(breakdownIndex) as FormGroup;
    const control = breakdownGroup.get(fieldName);
    if (control?.hasError("required")) {
      return "This field is required";
    }
    if (control?.hasError("min")) {
      return `Value must be greater than or equal to ${control.errors?.["min"].min}`;
    }
    if (control?.hasError("pattern")) {
      return "Please enter a valid number";
    }
    return "";
  }
  generateDynamicPlanId(): string {
  return 'plan-' + Math.random().toString(36).substring(2, 10) + Date.now();
}

}
